// Microbe RTS — floating (dynamic) camera joystick.
//
// Invisible until the map's pointer layer (ui/input.ts) calls begin(x,y): a thumb pad fades in
// CENTERED on that touch point and pans the camera by the drag offset — same edge-scroll curve the
// old fixed pad used — then hides on end(). It is purely visual DOM (pointer-events:none); input.ts
// owns the real pointer stream (so it can also arbitrate taps / pinch) and just feeds us the three
// lifecycle calls. begin() is only issued when the touch lands on open water with no unit selected.
//
// Talks to the renderer ONLY through the Camera contract (cam.pan is content-inverted, so we negate).

import { CAMERA } from '../config.js';
import type { Camera } from '../render/camera.js';

const RADIUS = 128; // px from the spawn center to the full-speed rim (larger pad = more thumb travel)
const DEADZONE = 0.18; // fraction of RADIUS held still around center, for precise small nudges

export interface FloatingJoystick {
  begin(clientX: number, clientY: number): void;
  move(clientX: number, clientY: number): void;
  end(): void;
  destroy(): void;
}

export function createFloatingJoystick(cam: Camera): FloatingJoystick {
  const pad = document.createElement('div');
  pad.className = 'floating-joystick';
  pad.setAttribute('aria-hidden', 'true');
  const knob = document.createElement('div');
  knob.className = 'joystick-knob';
  pad.appendChild(knob);
  document.body.appendChild(pad);

  let active = false;
  const center = { x: 0, y: 0 };
  const nvec = { x: 0, y: 0 }; // knob offset normalized to -1..1 of RADIUS
  let raf = 0;
  let last = 0;

  // 0 inside the deadzone, then a CUBIC ramp to 1 at the rim: small offsets near center barely
  // move the camera (fine scrubbing), and it accelerates hard only out toward the edge.
  const curve = (n: number): number => {
    const a = Math.abs(n);
    if (a <= DEADZONE) return 0;
    const m = (a - DEADZONE) / (1 - DEADZONE);
    const c = m < 1 ? m : 1;
    return Math.sign(n) * c * c * c;
  };

  const frame = (ts: number): void => {
    if (!active) {
      raf = 0;
      return;
    }
    const dt = last ? Math.min(0.05, (ts - last) / 1000) : 0;
    last = ts;
    const nx = curve(nvec.x);
    const ny = curve(nvec.y);
    if (nx !== 0 || ny !== 0) {
      const v = CAMERA.edgeScrollSpeed * dt;
      cam.pan(-nx * v, -ny * v); // non-inverted: camera pulls toward the knob direction
    }
    raf = requestAnimationFrame(frame);
  };

  const apply = (clientX: number, clientY: number): void => {
    const dx = clientX - center.x;
    const dy = clientY - center.y;
    const len = Math.hypot(dx, dy) || 0.0001;
    const ux = dx / len;
    const uy = dy / len;
    const mag = Math.min(len, RADIUS);
    nvec.x = (ux * mag) / RADIUS;
    nvec.y = (uy * mag) / RADIUS;
    const knobReach = RADIUS * 0.62;
    const kl = Math.min(len, knobReach);
    knob.style.transform = `translate(${ux * kl}px, ${uy * kl}px)`;
  };

  return {
    begin(clientX: number, clientY: number): void {
      active = true;
      center.x = clientX;
      center.y = clientY;
      pad.style.left = `${clientX}px`;
      pad.style.top = `${clientY}px`;
      nvec.x = 0;
      nvec.y = 0;
      knob.style.transform = 'translate(0px, 0px)';
      pad.classList.add('floating-joystick-on');
      last = 0;
      if (!raf) raf = requestAnimationFrame(frame);
    },
    move(clientX: number, clientY: number): void {
      if (!active) return;
      apply(clientX, clientY);
    },
    end(): void {
      active = false;
      nvec.x = 0;
      nvec.y = 0;
      pad.classList.remove('floating-joystick-on');
      if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
    },
    destroy(): void {
      if (raf) cancelAnimationFrame(raf);
      pad.remove();
    },
  };
}
